import { useEffect, useState, useRef } from 'react';
import { useReduxState } from 'hooks';
import { RichTextValue } from 'components/common';
import { Checkbox, FormGroup } from 'components/common/form';
import { Icon } from 'components/common/icons';
import { TaskPointWeightSelector } from 'components/common/tasks';
import { EditorBlockTranslationReference } from 'components/editor';
import {
  defaultBlockData,
  EditorBlockIsMandatorySettings,
  EditorBlockMoreSettings,
  EditorBlockRequireAnswerToProceedInfo
} from 'components/editor/blocks';
import { FillinEditor, TextEditor } from 'components/editor/inputs';
import { EditorBlockFillInAnswer } from '.';

import type { BlockFillin } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

export type BlockFillInStateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockFillIn = ({
  blockId,
  data,
  isAIPreview,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockFillin> & BlockFillInStateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { messages, theme } = useReduxState();

  const wrapperRef = useRef<HTMLDivElement>(null);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeContentId, setActiveContentId] = useState<string | null>(null);

  useEffect(() => {
    document.addEventListener('mousedown', handleDivClick);
    return () => {
      document.removeEventListener('mousedown', handleDivClick);
    };
  }, [activeId]);

  const handleAnswerFocus = (answerId: string) => {
    setActiveId(answerId);
  };

  const handleAnswersInputChange = (
    index: number,
    mappedData: { value: string }[]
  ) => {
    // when translating a language version, don't allow changes to answer structure
    if (editorLanguageActive) return;

    const updatedAnswers = [...data.correctAnswers];

    const answerId = updatedAnswers[index].id;

    updatedAnswers[index] = {
      ...updatedAnswers[index],
      options: mappedData.map((_, optIndex) => ({
        value: `{{ locales.translatable.correctAnswers_${answerId}_${
          optIndex + 1
        } }}`
      }))
    };

    // get a clone of the current locale data for the language
    const currentLocaleData = { ...data.locales[languageVersion].translatable };

    // remove old keys for this answer id from the locale data
    Object.keys(currentLocaleData).forEach((key) => {
      if (key.startsWith(`correctAnswers_${answerId}_`)) {
        delete currentLocaleData[key];
      }
    });

    // add new keys for each answer option
    mappedData.forEach((opt, optIndex) => {
      currentLocaleData[`correctAnswers_${answerId}_${optIndex + 1}`] =
        opt.value;
    });

    updateData({
      ...data,
      correctAnswers: updatedAnswers,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: currentLocaleData
        }
      }
    });
  };

  const handleAnswerTranslationChange = (
    answerId: string,
    optionIndex: number,
    newValue: string
  ) => {
    const localeKey = `correctAnswers_${answerId}_${optionIndex + 1}`;

    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            [localeKey]: newValue
          }
        }
      }
    });
  };

  const handleContentChange = (html: string) => {
    if (!html) return;

    // derive structure from
    const answerIds = Array.from(
      html.matchAll(/<answer[^>]*data-id="(\d+)"[^>]*><\/answer>/g)
    ).map((match) => match[1] || '');

    const contentDataParagraphs = (() => {
      const tempParagraphs = html.replace(/<\/p>/g, '').split(/<p[^>]*>/);

      const paragraphs = tempParagraphs.filter((p, index) => {
        return p.length || (index !== 0 && index !== tempParagraphs.length - 1);
      });

      const out = [] as any[];
      paragraphs.forEach((paragraph) => {
        const parts = paragraph
          .split(/(<answer[^>]*data-id="\d+"[^>]*><\/answer>)/g)
          .filter((part) => part !== '');

        const paragraphData = parts.map((part) => {
          const answerMatch = part.match(
            /<answer[^>]*data-id="(\d+)"[^>]*><\/answer>/
          );
          if (answerMatch) return { type: 'answer', id: answerMatch[1] };

          return { type: 'text', content: part };
        });
        out.push(paragraphData);
      });

      return out;
    })();

    let newActive: string | null = null;
    const newCorrectAnswers = answerIds.map((id) => {
      const existing = data.correctAnswers.find((x) => x.id === id);
      if (existing) return existing;

      newActive = id;
      return { id, options: [] };
    });

    const newData = {
      ...data,
      ...(!editorLanguageActive ? { correctAnswers: newCorrectAnswers } : {}),
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          contentData: contentDataParagraphs,
          translatable: {
            ...data.locales[languageVersion].translatable,
            content: html
          }
        }
      }
    };

    setActiveId(newActive);
    setActiveContentId(newActive);
    updateData(newData);
  };

  const handleDivClick = (ev: MouseEvent) => {
    let el = ev.target as HTMLElement;

    if (el?.classList?.contains('fill-in-editor-answer-clear')) return;

    if (wrapperRef.current && !wrapperRef.current.contains(el)) {
      setActiveId(null);
      setActiveContentId(null);
      return;
    }

    while (el && el !== wrapperRef.current && !el.hasAttribute('data-id')) {
      el = el.parentElement!;
    }
    if (!el || !el.hasAttribute('data-id')) {
      setActiveId(null);
      setActiveContentId(null);
      return;
    }

    const clickedId = el.getAttribute('data-id');
    if (activeId !== clickedId) {
      setActiveId(clickedId);
      setActiveContentId(clickedId);
    }
  };

  const handleEquivalenceChange = (value: boolean) => {
    updateData({ ...data, strictEquivalence: value });
  };

  const handleFeedbackChange = (id: string, html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            [id]: html
          }
        }
      }
    });
  };

  const handlePointsWeightChange = (value: number) => {
    updateData({ ...data, pointsWeight: value });
  };

  const handleQuestionChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            question: html
          }
        }
      }
    });
  };

  const parsePreviewContent = (content: string) => {
    // replace all <answer></answer> tags with spans
    return content.replace(
      /<answer[^>]*><\/answer>/g,
      '<span class="preview-content-placeholder"></span>'
    );
  };

  const feedback = [
    {
      id: 'feedbackCorrect',
      icon: 'check-circle' as const,
      iconColor: theme.successStandaloneColor,
      message: messages.editorBlockQuestionFeedbackCorrect,
      content: data.locales[languageVersion].translatable.feedbackCorrect || '',
      tooltip: null
    },
    {
      id: 'feedbackPartiallyCorrect',
      icon: 'question-circle' as const,
      iconColor: theme.warningColor,
      message: messages.editorBlockMultipleChoiceFeedbackPartiallyCorrect,
      content:
        data.locales[languageVersion].translatable.feedbackPartiallyCorrect ||
        '',
      tooltip: messages.editorBlockMultipleChoiceFeedbackPartiallyCorrectInfo
    },
    {
      id: 'feedbackIncorrect',
      icon: 'times-circle' as const,
      iconColor: theme.dangerColor,
      message: messages.editorBlockQuestionFeedbackIncorrect,
      content:
        data.locales[languageVersion].translatable.feedbackIncorrect || '',
      tooltip: null
    }
  ];

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  return (
    <div className="fill-in" ref={wrapperRef}>
      <FormGroup maxWidth="100%">
        {!isAIPreview ? (
          <TaskPointWeightSelector
            blockId={blockId}
            blockName={messages.blocksFillIn}
            disabled={isPreview || isEditingLanguageVersion}
            isMandatory={data.isMandatory}
            pointsWeight={
              data.hasOwnProperty('pointsWeight')
                ? data.pointsWeight
                : defaultBlockData.fillin.pointsWeight
            }
            onSelection={handlePointsWeightChange}
          />
        ) : null}

        {isAIPreview || isPreview ? (
          <RichTextValue
            html={data.locales[languageVersion].translatable.question}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable?.question ||
                ''
              }
              translationHtml={
                data.locales[languageVersion].translatable.question
              }
            >
              <TextEditor
                ariaLabel={messages.commonQuestion}
                backgroundColor="inherit"
                content={data.locales[languageVersion].translatable.question}
                onChange={handleQuestionChange}
                padding="0"
                placeholder={messages.commonAddTaskDescription}
              />
            </EditorBlockTranslationReference>
          </>
        )}
      </FormGroup>

      {isAIPreview || isPreview ? (
        <FormGroup label={messages.commonContent} maxWidth="100%">
          <div className="preview-content">
            <RichTextValue
              html={parsePreviewContent(
                data.locales[languageVersion].translatable.content || ''
              )}
            />
          </div>
        </FormGroup>
      ) : (
        <FormGroup label={messages.blocksFillInContent} maxWidth="100%">
          <EditorBlockTranslationReference
            backgroundColor={theme.lightGrey}
            isInnerElement={true}
            referenceHtml={(
              data.locales[editorLanguageReference]?.translatable?.content || ''
            ).replace(
              /<answer[^>]*data-id="([^"]+)"[^>]*><\/answer>/g,
              `<span style="` +
                `display:inline-block;` +
                `border:1px solid ${theme.lightGreyDarkActive};` +
                `border-radius:4px;` +
                `padding:0 20px;` +
                `height:1.6rem;` +
                `vertical-align:-0.3rem;` +
                `background:#fff;` +
                `"></span>`
            )}
            style={{ marginTop: 5 }}
            translationHtml={
              data.locales[editorLanguageReference]?.translatable?.content || ''
            }
          >
            <FillinEditor
              activeId={activeId}
              content={data.locales[languageVersion].translatable.content}
              onChange={handleContentChange}
              translationReferenceContent={
                editorLanguageActive
                  ? data.locales[editorLanguageReference]?.translatable
                      ?.content || ''
                  : undefined
              }
              {...stateProps}
            />
          </EditorBlockTranslationReference>
        </FormGroup>
      )}

      {isAIPreview || isPreview ? (
        <FormGroup
          label={messages.commonCorrectAnswers}
          style={{ marginTop: 25 }}
          maxWidth="100%"
        >
          {data.correctAnswers && data.correctAnswers.length > 0 ? (
            data.correctAnswers.map((answers, i: number) => (
              <div className="preview-answer" key={`preview-answer-${i}`}>
                <span className="preview-answer-number">{`${i + 1}.`}</span>
                {answers.options && answers.options.length > 0 ? (
                  answers.options.map((option, index: number) => {
                    const localeKey = `correctAnswers_${answers.id}_${
                      index + 1
                    }`;
                    const localizedValue =
                      data.locales[languageVersion].translatable[localeKey] ||
                      option.value;
                    return (
                      <span className="preview-answer-option" key={index}>
                        {localizedValue}
                      </span>
                    );
                  })
                ) : (
                  <span>{messages.tasksNoAnswers}</span>
                )}
              </div>
            ))
          ) : (
            <span>{messages.tasksNoAnswers}</span>
          )}
        </FormGroup>
      ) : (
        data.correctAnswers &&
        data.correctAnswers.length > 0 && (
          <FormGroup
            label={messages.commonCorrectAnswers}
            style={{ marginTop: 25 }}
            maxWidth="100%"
          >
            {data.correctAnswers.map((answers, i: number) => (
              <EditorBlockFillInAnswer
                data={answers}
                locales={data.locales}
                languageVersion={languageVersion}
                id={`fillin-correct-answer-${blockId}-${i}`}
                index={i}
                key={`answer-${blockId}-${i}`}
                onInputChange={handleAnswersInputChange}
                onFocus={handleAnswerFocus}
                onTranslationChange={handleAnswerTranslationChange}
                activeContentId={activeContentId}
                {...stateProps}
              />
            ))}
          </FormGroup>
        )
      )}

      {!isAIPreview ? (
        <FormGroup
          label={isPreview ? null : messages.commonSettings}
          style={{ marginTop: isPreview ? 12 : 25, width: 'max-content' }}
          maxWidth="100%"
        >
          <Checkbox
            aria-label={messages.editorBlockFillInStrict}
            disabled={isPreview || isEditingLanguageVersion}
            field="equivalence"
            id={`${blockId}-equivalence`}
            labelProps={{
              'data-tooltip': messages.editorBlockFillInStrictInfo
            }}
            onValueChange={handleEquivalenceChange}
            label={
              <span>
                {messages.editorBlockFillInStrict}
                <Icon icon="question-circle" style={{ marginLeft: 5 }} />
              </span>
            }
            initialValue={data.strictEquivalence}
          />
        </FormGroup>
      ) : null}

      {feedback.map((item) => (
        <FormGroup
          key={`feedback-${blockId}-${item.id}`}
          label={
            <div className="result-label" data-tooltip={item.tooltip || ''}>
              <div className="result-label-icon">
                <Icon icon={item.icon} style={{ color: item.iconColor }} />
              </div>
              {item.message}
            </div>
          }
          maxWidth="100%"
        >
          {isAIPreview || isPreview ? (
            <RichTextValue
              html={item.content || '-'}
              style={{
                backgroundColor: 'white',
                border: `1px solid ${theme.lightGreyDark}`,
                borderRadius: theme.inputBorderRadius,
                color: theme.textColorLight,
                padding: 10
              }}
            />
          ) : (
            <>
              <EditorBlockTranslationReference
                backgroundColor={theme.lightGrey}
                isInnerElement={true}
                referenceHtml={
                  data.locales[editorLanguageReference]?.translatable?.[
                    item.id
                  ] || ''
                }
                style={{ marginTop: 5, marginBottom: 5 }}
                translationHtml={item.content}
              >
                <TextEditor
                  content={item.content}
                  onChange={(value: string) =>
                    handleFeedbackChange(item.id, value)
                  }
                  placeholder={messages.commonFeedbackPlaceholder}
                  showBorder={true}
                />
              </EditorBlockTranslationReference>
            </>
          )}
        </FormGroup>
      ))}

      {!isAIPreview && !isEditingLanguageVersion ? (
        <EditorBlockMoreSettings
          settingsHaveBeenEdited={!isPreview && Boolean(data?.isMandatory)}
        >
          <EditorBlockIsMandatorySettings
            blockId={blockId}
            data={data}
            disabled={isPreview}
            updateData={updateData}
          />
        </EditorBlockMoreSettings>
      ) : null}

      {!isAIPreview ? (
        <EditorBlockRequireAnswerToProceedInfo data={data} />
      ) : null}

      <style jsx>{`
        .fill-in {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
          counter-reset: fillin-answers;
          padding: 15px;
        }
        .result-label {
          align-items: center;
          display: flex;
        }
        .result-label-icon {
          font-size: 1.1rem;
          margin-right: 5px;
          position: relative;
          top: 1px;
        }
        .preview-content {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          padding: 10px 15px;
        }
        .preview-content :global(.preview-content-placeholder) {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.inputBorderRadius};
          display: inline-block;
          margin: 0 5px;
          padding: 3px 8px;
          width: 80px;
        }
        .preview-content :global(.preview-content-placeholder::before) {
          counter-increment: fillin-answers;
          content: counter(fillin-answers) '.';
          color: ${theme.textColorLight};
        }
        .preview-answer {
          background-color: white;
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.inputBorderRadius};
          color: ${theme.textColorLight};
          margin-bottom: 5px;
          padding: 8px 10px;
        }
        .preview-answer-number {
          margin-right: 10px;
        }
        .preview-answer-option {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.inputBorderRadius};
          display: inline-block;
          font-size: 1.5rem;
          font-weight: 600;
          padding: 5px 10px;
          margin-right: 10px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockFillIn;
